import Alpine from "alpinejs";
import * as API from "../api";
import * as Process from "../../utils/process";

import { openEmailCustomizerFromForm } from "../email_form";
import { isPendingSignature } from "../signing";

/** @const {number} POLL_INTERVAL The polling interval in ms */
const POLL_INTERVAL = 5000;

/** @const {number} ERROR_TIMEOUT The timeout before showing an error, helps avoid having users bruteforcing the button if the service is down */
const ERROR_TIMEOUT = 1000;

Alpine.data(
  "documentButton",
  ({
    initGenerateUrl,
    initStatus,
    initGeneratedTemplateName,
    initGeneratedFile,
    disabled,
    canRegenerate,
    zipContentsCompositeIdentifiers,
  }) => ({
    generatedFile: initGeneratedFile,
    generateUrl: initGenerateUrl,
    status: initStatus,
    generatedTemplateName: initGeneratedTemplateName,
    hoverDownload: false,
    filename: null,
    fileUrl: null,
    skillsSentAt: null,
    emailSentAt: null,
    extranetPublishedAt: null,
    signedAt: null,
    signingState: null,
    generatedAt: null,
    requestedFormat: null,
    zipContentsCompositeIdentifiers: zipContentsCompositeIdentifiers,
    init() {
      this.setFromGeneratedFile();

      // As of the removal of the old document button system (09/2024), signing state / polling still happens in
      // assets/app_js/framework/generate_doc_button.js
      //
      // This should be moved here to complete the refactor.
      window.addEventListener("digiforma:signing_state_change", (event) => {
        if (event.detail.generated_file_id === this.generatedFile?.id) {
          this.signingState = event.detail.state;
        }
      });
    },
    setFromGeneratedFile() {
      this.emailSentAt = this.generatedFile && this.generatedFile.email_sent_at;
      this.skillsSentAt = this.generatedFile && this.generatedFile.skills_sent_at;
      this.extranetPublishedAt = this.generatedFile && this.generatedFile.extranet_published_at;
      this.signedAt = this.generatedFile && this.generatedFile.signed && this.generatedFile.updated_at;
      this.generatedAt = this.generatedFile && this.generatedFile.inserted_at;
      this.signingState = this.generatedFile && this.generatedFile.signing_request && this.generatedFile.signing_request.state;
      this.fileUrl = this.generatedFile && this.generatedFile.filepath;
      // zips don't have an original_filename
      this.filename =
        this.generatedFile && (this.generatedFile.original_filename || `${this.generatedFile.type}.${this.generatedFile.ext}`);
      // this is used for a zip document button to be able to find the documents it contains and track their signature state
      this.$el.dataset.compositeIdentifier = this.generatedFile && this.generatedFile.composite_identifier;
    },
    get canRegenerate() {
      return !this.disabled && (canRegenerate == true || this.generatedFile == null) && !this.signaturePending;
    },
    get signaturePending() {
      return isPendingSignature(this.signingState);
    },
    get disabled() {
      return disabled || this.status === "starting";
    },
    get fileExtension() {
      if (this.filename) {
        return this.filename.split(".").slice(-1)[0];
      }
      return null;
    },
    get signingRequestProofUrl() {
      if (this.generatedFile) {
        return p`/signing_requests/${this.generatedFile.id}/download_evidences`;
      }
      return null;
    },
    async requestGeneration(templateId, format, templateName, trainingSessionId) {
      this.requestedFormat = format;

      // Absence report advice form: if it has unsaved changes, offer to save it before generating the
      // document. The save button's own `disabled` state (kept in sync by the LiveView) is the source
      // of truth for "dirty" here, rather than a value mirrored across the LiveView/Alpine boundary.
      const adviceForm = this.$refs.saveAdviceBeforeGeneratePopup && document.querySelector("[data-absence-advice-form]");
      const adviceSaveButton = adviceForm && adviceForm.querySelector("button[type='submit']");

      if (adviceSaveButton && !adviceSaveButton.disabled) {
        if (await this.$popup.confirm(this.$refs.saveAdviceBeforeGeneratePopup)) {
          const adviceSaved = new Promise((resolve) => window.addEventListener("advice-saved", resolve, { once: true }));
          adviceForm.requestSubmit();
          await adviceSaved;
        }
      }

      if (
        this.generatedFile &&
        this.signingState &&
        this.$refs.regeneratePopup &&
        !(await this.$popup.confirm(this.$refs.regeneratePopup))
      ) {
        return;
      }

      const { anyPendingSignature, anySigned } = this.zipContentsCompositeIdentifiers.reduce(
        ({ anyPendingSignature, anySigned }, identifier) => {
          const documentButton = document.querySelector(`[data-composite-identifier="${identifier}"]`);

          // button did not have a generated document and thus no composite-identifier was set
          if (!documentButton) {
            return { anyPendingSignature, anySigned };
          }

          const { signingState, signedAt } = Alpine.$data(documentButton);
          return {
            anyPendingSignature: anyPendingSignature || isPendingSignature(signingState),
            anySigned: anySigned || !!signedAt,
          };
        },
        { anyPendingSignature: false, anySigned: false }
      );

      if (anyPendingSignature) {
        if (this.$refs.zipWarningPopupPending && !(await this.$popup.confirm(this.$refs.zipWarningPopupPending))) {
          return;
        }
      }

      if (anySigned) {
        if (this.$refs.zipWarningPopupSigned && !(await this.$popup.confirm(this.$refs.zipWarningPopupSigned))) {
          return;
        }
      }

      if (this.$refs.docxWarningPopup && format === "docx" && !(await this.$popup.confirm(this.$refs.docxWarningPopup))) {
        return;
      }

      const previousStatus = this.status;

      this.status = "starting"; // Optimistically set the status to the one we should get from the generate request => this shows loading spinner for the user
      this.open = false; // Closes template choice overflow menu

      API.fetchJSON(this.generateUrl, {
        method: "POST",
        body: {
          template: templateId,
          format: format,
          // only send trainingSessionId if it's the first time the file is generated or if the file is a zip
          trainingSessionId: (!this.generatedFile || this.generatedFile.struct_type == "TrainingSession") && trainingSessionId,
        },
      })
        .then((body) => {
          this.status = body.status;
          const pollUrl = p`/ajax/generated_file_job_status/${body.job_id}`;
          return this.poll(pollUrl);
        })
        .then((body) => {
          this.status = body.status;
          this.generatedFile = body.generated_file;

          this.setFromGeneratedFile();

          // zips don't have an original_filename
          this.filename = body.generated_file.original_filename || `${body.generated_file.type}.${body.generated_file.ext}`;

          this.generatedTemplateName = templateName;

          // Update the email form to use the new generated file
          if (this.$refs.emailForm) {
            this.$refs.emailForm.querySelector("input[name='generated_file_id']").value = body.generated_file.id;
            this.$refs.emailForm.dataset.attachment = JSON.stringify({
              id: body.generated_file.id,
              name: body.generated_file.original_filename,
              filepath: body.generated_file.filepath,
            });
          }

          this.$toast.show(this.$refs.successToast);
        })
        .catch(() => {
          // Wait a bit before showing the error and updating status (to keep the spinner running)
          // This helps avoid user bruteforcing the button

          setTimeout(() => {
            this.$toast.show(this.$refs.errorToast);
            this.status = previousStatus;
          }, ERROR_TIMEOUT);
        });
    },
    async poll(url) {
      // Start by sleeping, this polling function is called right after the generation
      // request, and wo know the backend won't ever be _that_ fast to generate the
      // document.
      await Process.sleep(POLL_INTERVAL);

      return API.pollJSONUntil(
        (body) => {
          if (body.status == "failure") {
            throw body;
          } else {
            return body.status == "available";
          }
        },
        url,
        undefined,
        POLL_INTERVAL
      );
    },
    onEmail() {
      openEmailCustomizerFromForm(this.$refs.emailForm, {
        title: this.$el.dataset.emailTitle,
        attachmentTagParam: this.$el.dataset.emailAttachmentTagParam,
      });
    },
  })
);
